import { createTheme, Theme as MuiTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Theme {
    colors: {
      scrollbar: React.CSSProperties['color'];
      inputText: React.CSSProperties['color'];
      container: React.CSSProperties['color'];
      containerState: React.CSSProperties['color'];
      chipsDefault: React.CSSProperties['color'];
      circular: React.CSSProperties['color'];
      tableHeader: React.CSSProperties['color'];
      tableExpandRow: React.CSSProperties['color'];
      sideBarMenu: React.CSSProperties['color'];
      menuItemHover: React.CSSProperties['color'];
      menuItemSelected: React.CSSProperties['color'];
      menuSubItemHover: React.CSSProperties['color'];
      menuSubItemSelected: React.CSSProperties['color'];
      iconGrey: React.CSSProperties['color'];
      iconMain: React.CSSProperties['color'];
      iconSecondary: React.CSSProperties['color'];
      iconPressed: React.CSSProperties['color'];
      loaderFill: React.CSSProperties['color'];
      greyBackground: React.CSSProperties['color'];
      transparent12: React.CSSProperties['color'];
      modalError: React.CSSProperties['color'];
    };
    customVars: {
      cardShadow: React.CSSProperties['boxShadow'];
      dropDownShadow: React.CSSProperties['boxShadow'];
      multiSelectMenuShadow: React.CSSProperties['boxShadow'];
      headerShadow: React.CSSProperties['boxShadow'];
      gradient: React.CSSProperties['background'];
    };
  }

  interface ThemeOptions {
    colors: {
      scrollbar: React.CSSProperties['color'];
      inputText: React.CSSProperties['color'];
      container: React.CSSProperties['color'];
      containerState: React.CSSProperties['color'];
      chipsDefault: React.CSSProperties['color'];
      circular: React.CSSProperties['color'];
      tableHeader: React.CSSProperties['color'];
      tableExpandRow: React.CSSProperties['color'];
      sideBarMenu: React.CSSProperties['color'];
      menuItemHover: React.CSSProperties['color'];
      menuItemSelected: React.CSSProperties['color'];
      menuSubItemHover: React.CSSProperties['color'];
      menuSubItemSelected: React.CSSProperties['color'];
      iconGrey: React.CSSProperties['color'];
      iconMain: React.CSSProperties['color'];
      iconSecondary: React.CSSProperties['color'];
      iconPressed: React.CSSProperties['color'];
      loaderFill: React.CSSProperties['color'];
      greyBackground: React.CSSProperties['color'];
      transparent12: React.CSSProperties['color'];
      modalError: React.CSSProperties['color'];
    };
    customVars: {
      cardShadow: React.CSSProperties['boxShadow'];
      dropDownShadow: React.CSSProperties['boxShadow'];
      multiSelectMenuShadow: React.CSSProperties['boxShadow'];
      headerShadow: React.CSSProperties['boxShadow'];
      gradient: React.CSSProperties['background'];
    };
  }

  interface TypographyVariants {
    input: React.CSSProperties;
    main: React.CSSProperties;
    tableHeader: React.CSSProperties;
    tableRow: React.CSSProperties;
    helpText: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    input?: React.CSSProperties;
    main?: React.CSSProperties;
    tableHeader?: React.CSSProperties;
    tableRow?: React.CSSProperties;
    helpText?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    input: true;
    main: true;
    tableHeader: true;
    tableRow: true;
    helpText: true;
    h3: false;
    h4: false;
    h5: false;
    h6: false;
    body1: false;
    body2: false;
    caption: false;
    subtitle1: false;
    subtitle2: false;
    overline: false;
  }
}

//* FONT VARIABLES
const HTML_FONT_SIZE = 10;
const REGULAR_FONT_WEIGHT = 400;
const SEMI_BOLD_FONT_WEIGHT = 600;

//* MAIN COLORS
const PRIMARY = '#C99700';
const PRIMARY_LIGHT = '#D2A829';
const PRIMARY_DARK = '#BD8E00';
const SECONDARY = '#0045B9';
const SECONDARY_LIGHT = '#FAFBFE';
const SECONDARY_DARK = '#F0F4FB';
const ERROR = '#A90000';
const SUCCESS = '#1D9B42';
const NEUTRAL = '#444648';
const DISABLED = '#E9E9E9';
const MODAL_ERROR = '#F1D6D6';

//* TEXT COLORS
const MAIN_TEXT = '#333333';
const SECONDARY_TEXT = '#767676';
const DISABLED_TEXT = '#C7C7C7';
const INPUT_TEXT = '#808080';

//* SUPPORTING COLORS
// const TRANSPARENT = 'rgba(0, 0, 0, 0)';
const TRANSPARENT_12 = 'rgba(0, 0, 0, 0.12)';
const TRANSPARENT_14 = 'rgba(0, 0, 0, 0.14)';
const TRANSPARENT_24 = 'rgba(0, 0, 0, 0.24)';
const TRANSPARENT_16 = 'rgba(0, 0, 0, 0.16)';
const TRANSPARENT_20 = 'rgba(0, 0, 0, 0.20)';
const TRANSPARENT_04 = 'rgba(0, 0, 0, 0.04)';
const SCROLLBAR = '#F1F1F1';
const BACKGROUND = '#FCFCFC';
const CONTAINER = '#A3ABB2';
const BLUE_BG = '#005FFF';
const GREY_BG = '#FAFAFA';
const ICON_PRESSED = '#C4C4C4';

//* DATA TABLE COLORS
const HEADER = '#F2F5F9';
const EXPAND_ROW = '#EAEFF7';

//* SIDE BAR MENU
const SIDE_BAR_MENU = '#212E7C';
const MENU_HOVER = '#333F86';
const MENU_SELECTED = '#3C478C';
const MENU_SUB_ITEM_HOVER = '#333F86';
const MENU_SUB_ITEM_SELECTED = '#495494';

//* ICON COLORS
const GREY = '#767676';
const MAIN_ICON = '#333333';
const SECONDARY_ICON = '#0045B9';

//* CIRCULAR COLORS
const CIRCULAR = '#AEBFDA';

//* GLOBAL COLORS
const BLACK = '#000000';
const WHITE = '#FFFFFF';
const PINK = '#F9F3E0';

let theme = createTheme({
  colors: {
    scrollbar: SCROLLBAR,
    inputText: INPUT_TEXT,
    container: CONTAINER,
    containerState: MAIN_TEXT,
    chipsDefault: PINK,
    circular: CIRCULAR,
    tableHeader: HEADER,
    tableExpandRow: EXPAND_ROW,
    sideBarMenu: SIDE_BAR_MENU,
    menuItemHover: MENU_HOVER,
    menuItemSelected: MENU_SELECTED,
    menuSubItemHover: MENU_SUB_ITEM_HOVER,
    menuSubItemSelected: MENU_SUB_ITEM_SELECTED,
    iconGrey: GREY,
    iconMain: MAIN_ICON,
    iconSecondary: SECONDARY_ICON,
    iconPressed: ICON_PRESSED,
    loaderFill: TRANSPARENT_04,
    greyBackground: GREY_BG,
    transparent12: TRANSPARENT_12,
    modalError: MODAL_ERROR,
  },
  palette: {
    common: {
      black: BLACK,
      white: WHITE,
    },
    primary: {
      main: PRIMARY,
      light: PRIMARY_LIGHT,
      dark: PRIMARY_DARK,
      contrastText: BLACK,
    },
    secondary: {
      main: SECONDARY,
      light: SECONDARY_LIGHT,
      dark: SECONDARY_DARK,
      contrastText: WHITE,
    },
    background: {
      paper: BACKGROUND,
      default: WHITE,
    },
    text: {
      primary: MAIN_TEXT,
      secondary: SECONDARY_TEXT,
      disabled: DISABLED_TEXT,
    },
    error: {
      main: ERROR,
    },
    info: {
      main: NEUTRAL,
    },
    success: {
      main: SUCCESS,
    },
    action: {
      disabled: DISABLED_TEXT,
      disabledBackground: DISABLED,
    },
    grey: {
      600: GREY,
    },
  },
  // Using base spacing of 1rem and apply the spacing relative to that
  spacing: (factor: number) => `${factor * 1}rem`,
  typography: {
    htmlFontSize: HTML_FONT_SIZE,
    fontWeightRegular: REGULAR_FONT_WEIGHT,
    fontWeightBold: SEMI_BOLD_FONT_WEIGHT,
    fontFamily: ['"Open Sans"', 'Arial', 'Helvetica', 'sans-serif'].join(','),
    h1: {
      fontSize: '2.4rem',
      color: MAIN_TEXT,
      fontWeight: SEMI_BOLD_FONT_WEIGHT,
      lineHeight: '3.3rem',
    },
    h2: {
      fontSize: '1.6rem',
      color: MAIN_TEXT,
      fontWeight: SEMI_BOLD_FONT_WEIGHT,
      lineHeight: '2.2rem',
    },
    input: {
      fontSize: '1.6rem',
      color: MAIN_TEXT,
      fontWeight: REGULAR_FONT_WEIGHT,
      lineHeight: '2.2rem',
    },
    main: {
      fontSize: '1.4rem',
      color: MAIN_TEXT,
      fontWeight: REGULAR_FONT_WEIGHT,
      lineHeight: '1.9rem',
    },
    tableHeader: {
      fontSize: '1.4rem',
      color: MAIN_TEXT,
      fontWeight: SEMI_BOLD_FONT_WEIGHT,
      lineHeight: '1.9rem',
    },
    tableRow: {
      fontSize: '1.4rem',
      color: MAIN_TEXT,
      fontWeight: REGULAR_FONT_WEIGHT,
      lineHeight: '1.9rem',
    },
    button: {
      fontSize: '1.4rem',
      fontWeight: SEMI_BOLD_FONT_WEIGHT,
      lineHeight: '1.9rem',
    },
    helpText: {
      fontSize: '1.2rem',
      color: MAIN_TEXT,
      fontWeight: REGULAR_FONT_WEIGHT,
      lineHeight: '1.6rem',
    },
  },
  // z-index of appBar (default: 1100) should be more than drawer
  zIndex: {
    drawer: 1200,
    appBar: 1201,
  },
  // Variables
  customVars: {
    cardShadow: `0 0.1rem 0.1rem ${TRANSPARENT_14}, 0 0.2rem 0.1rem ${TRANSPARENT_12}, 0 0.1rem 0.3rem ${TRANSPARENT_20}`,
    dropDownShadow: `0 0.1rem 0.2rem ${TRANSPARENT_12}`,
    multiSelectMenuShadow: `0 0.1rem 0.2rem ${TRANSPARENT_24}`,
    headerShadow: `0 0.1rem 0.3rem ${TRANSPARENT_16}`,
    gradient: ` linear-gradient(0deg, ${TRANSPARENT_20}, ${TRANSPARENT_20}), linear-gradient(153.69deg, ${BLUE_BG} 0%, ${SIDE_BAR_MENU} 100%)`,
  },
});

theme = createTheme(theme, {
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          fontSize: '62.5%', // 1rem = 10px
        },
        // Cursor not allowed for disabled states globally
        '.Mui-disabled': {
          cursor: 'not-allowed',
        },
        // Scrollbar styles
        '*': {
          scrollbarWidth: 'thin',
        },
        '*::-webkit-scrollbar': {
          width: '1rem',
          height: '1rem',
        },
        '*::-webkit-scrollbar-track': {
          backgroundColor: theme.colors.scrollbar,
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundClip: 'content-box',
          backgroundColor: theme.colors.iconGrey,
          borderRadius: '5rem',
          border: '0.1rem solid transparent',
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: theme.colors.circular,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          minWidth: '15.4rem',
          minHeight: '4rem',
          padding: '0.8rem 1.6rem',
        },
        contained: {
          '&:hover': {
            backgroundColor: theme.palette.primary.light,
          },
          '&:active': {
            backgroundColor: theme.palette.primary.dark,
          },
        },
        outlined: {
          '&.Mui-disabled': {
            borderColor: theme.palette.action.disabledBackground,
          },
          '&:hover': {
            backgroundColor: theme.palette.secondary.light,
          },
          '&:active': {
            backgroundColor: theme.palette.secondary.dark,
          },
        },
        text: {
          minWidth: '12.3rem',
          padding: '0.8rem',
          '&:hover': {
            backgroundColor: theme.palette.secondary.light,
          },
          '&:active': {
            backgroundColor: theme.palette.secondary.dark,
          },
        },
        startIcon: {
          marginLeft: '0',
          '*:nth-of-type(1)': {
            fontSize: '2.4rem',
          },
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h1',
          h2: 'h2',
          input: 'p',
          main: 'p',
          tableHeader: 'h2',
          tableRow: 'span',
          helpText: 'p',
        },
      },
      styleOverrides: {
        root: {
          wordBreak: 'break-word',
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontSize: '2.4rem',
        },
        fontSizeSmall: {
          fontSize: '1.6rem',
        },
        fontSizeLarge: {
          fontSize: '3.2rem',
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: '0',
          '&.Mui-disabled': {
            color: theme.palette.action.disabledBackground,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: '0',
          '&.Mui-disabled': {
            color: theme.palette.action.disabledBackground,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: theme.palette.text.primary,
          '&.Mui-focused': {
            color: theme.palette.text.primary,
          },
          '&.Mui-error': {
            color: theme.palette.error.main,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginTop: '0',
          color: theme.palette.text.primary,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        InputLabelProps: {
          shrink: true,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: theme.spacing(5.6),
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: theme.spacing(0.1),
            borderColor: theme.colors.containerState,
          },

          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.error.main,
          },

          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.action.disabledBackground,
          },
        },
        notchedOutline: {
          borderWidth: theme.spacing(0.1),
          borderColor: theme.colors.container,
        },
        sizeSmall: {
          minHeight: theme.spacing(4),
        },
        input: {
          padding: theme.spacing(1.6),
          color: theme.palette.text.primary,

          '&::placeholder': {
            color: theme.colors.inputText,
            opacity: 1,
          },
        },
        inputSizeSmall: {
          padding: theme.spacing(0.8, 1.6),
        },
        adornedEnd: {
          padding: '0',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.MuiMenuItem-root': {
            minHeight: theme.spacing(4),
            padding: theme.spacing(0.8, 2.4),
            backgroundColor: theme.palette.common.white,
          },
          '&.MuiMenuItem-root.Mui-selected': {
            backgroundColor: theme.palette.common.white,
          },
          '&.MuiMenuItem-root:hover': {
            backgroundColor: theme.palette.secondary.light,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        listbox: {
          backgroundColor: theme.palette.common.white,
          maxHeight: '40vh',
          '& .MuiAutocomplete-option': {
            padding: theme.spacing(0.8, 2.4),
            backgroundColor: theme.palette.common.white,
          },
          '& .MuiAutocomplete-option.Mui-focused': {
            backgroundColor: theme.palette.secondary.light,
          },
          '& .MuiAutocomplete-option[aria-selected="true"]': {
            backgroundColor: theme.palette.common.white,
          },
          '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
            backgroundColor: theme.palette.secondary.light,
          },
        },
        noOptions: {
          padding: theme.spacing(1.6, 2.4),
          backgroundColor: theme.palette.background.default,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: theme.customVars.dropDownShadow,
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        classes: {
          popper: 'styledToolTip',
        },
      },
      styleOverrides: {
        popper: {
          '&.styledToolTip[data-popper-placement*="top"] .MuiTooltip-tooltipPlacementTop':
            {
              margin: '0 0 0.4rem',
            },
        },
        tooltip: {
          maxWidth: 'max-content',
          padding: '1.6rem',
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.background.default,
          borderRadius: theme.shape.borderRadius,
          boxShadow: theme.customVars.headerShadow,
          fontSize: '1.4rem',
        },
      },
    },
  },
});

const getMaterialTheme = (): MuiTheme => theme;

export default getMaterialTheme;
